import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  EmojiEvents as AwardIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";

const FdpOngoing = ({ events = [] }) => {
  const navigate = useNavigate();
  const [loadingDomainId, setLoadingDomainId] = useState(null);
  const [buttonStatuses, setButtonStatuses] = useState({}); // { [facultyId]: { upload_button: boolean, continue_button: boolean, is_eligible: any } }
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [certificateModal, setCertificateModal] = useState({
    open: false,
    pdfUrl: null,
    loading: false,
    bookslotId: null,
  });

  const displayEvents = events;

  useEffect(() => {
    const fetchAllStatuses = async () => {
      if (!events || events.length === 0) {
        setLoadingStatuses(false);
        return;
      }

      setLoadingStatuses(true);
      const statuses = {};

      await Promise.all(
        events.map(async (item) => {
          const facultyId = item.faculty_id;
          if (!facultyId) return;

          try {
            const response = await axios.get(
              `${BASE_URL}/event/upload_continue_status/${facultyId}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                },
              }
            );
            statuses[facultyId] = {
              upload_button: response.data?.upload_button ?? false,
              continue_button: response.data?.continue_button ?? false,
            };
          } catch (err) {
            console.error(`Error checking upload/continue status for faculty ${facultyId}:`, err);
            // Default to false in case of error
            statuses[facultyId] = {
              upload_button: false,
              continue_button: false,
            };
          }
        })
      );

      setButtonStatuses(statuses);
      setLoadingStatuses(false);
    };

    fetchAllStatuses();
  }, [events]);

  const isEligibleForDownload = (item) => {
    return item?.is_eligible >= 1 && item?.status >= 1;
  };

  const fetchCertificate = async (bookslotId, eventType) => {
    setCertificateModal({
      open: true,
      pdfUrl: null,
      loading: true,
      bookslotId,
    });

    try {
      // Use different endpoint based on event type (same as completed tab)
      const endpoint =
        eventType?.toLowerCase() === "fdp"
          ? `${BASE_URL}/event/fdp-faculty-certificate/${bookslotId}`
          : `${BASE_URL}/event/edp-faculty-certificate/${bookslotId}`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch certificate");
      }

      const data = await response.json();
      const base64Data = data.certificate_base64;

      // Convert base64 to blob and create URL
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setCertificateModal((prev) => ({
        ...prev,
        pdfUrl: url,
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching certificate:", error);
      setCertificateModal({
        open: false,
        pdfUrl: null,
        loading: false,
        bookslotId: null,
      });
      alert("Failed to load certificate. Please try again.");
    }
  };

  const handleDownloadCertificate = () => {
    if (certificateModal.pdfUrl) {
      const link = document.createElement("a");
      link.href = certificateModal.pdfUrl;
      link.download = `certificate_${certificateModal.bookslotId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCloseCertificateModal = () => {
    if (certificateModal.pdfUrl) {
      URL.revokeObjectURL(certificateModal.pdfUrl);
    }
    setCertificateModal({
      open: false,
      pdfUrl: null,
      loading: false,
      bookslotId: null,
    });
  };

  if (displayEvents.length === 0) {
    return (
      <Box p={2}>
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "14px",
            border: "1px solid #E5E7EB",
            boxShadow: "0px 1px 6px rgba(0,0,0,0.04)",
            backgroundColor: "#fff",
          }}
        >
          <Typography variant="body1" sx={{ color: "#6B7280" }}>
            No ongoing events found.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check for redirect_link from the API before navigating to ProcessPage
  const handleContinueLearning = async (domainId, bookSlotId) => {
    try {
      setLoadingDomainId(domainId);

      const response = await axios.get(
        `${BASE_URL}/event/get-process-document`,
        {
          params: { domain_id: domainId, bookslot_id: bookSlotId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      // If backend returns a redirect_link, redirect externally
      if (response.data?.redirect_link) {
        console.log("redirect", response.data.redirect_link);
        localStorage.clear();
        window.location.href = response.data.redirect_link;
        return;
      }

      // No redirect_link found — navigate to ProcessPage normally
      navigate(`/process/${domainId}/${bookSlotId}`);
    } catch (err) {
      console.error("Error checking redirect:", err);
      // On error, still navigate to ProcessPage as fallback
      navigate(`/process/${domainId}/${bookSlotId}`);
    } finally {
      setLoadingDomainId(null);
    }
  };

  return (
    <Box p={2}>
      {displayEvents.map((item, idx) => (
        <Box key={idx} sx={{ mb: idx < displayEvents.length - 1 ? 3 : 0 }}>
          {/* Top Right: Download Certificate button (when eligible) */}
          {isEligibleForDownload(item) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <Button
                variant="contained"
                startIcon={<AwardIcon />}
                onClick={() =>
                  fetchCertificate(
                    item.bookslot_id || item.bookslotId,
                    item.event_type || item.eventType
                  )
                }
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor: "#1976d2",
                  px: 3,
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                Download Certificate
              </Button>
            </Box>
          )}

          {/* 3 Boxes in one row */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: "14px",
                  backgroundColor: "#eef2f7",
                  boxShadow: "0px 1px 6px rgba(0,0,0,0.04)",
                  border: "1px solid #edf1f5",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize="15px"
                  mb={1.5}
                >
                  Course Details
                </Typography>
                <Typography fontSize="13px" mb={1}>
                  <strong>Domain Name:</strong> {item.event_domain_name}
                </Typography>
                <Typography fontSize="13px">
                  <strong>Event Type:</strong> {item.event_type}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: "14px",
                  backgroundColor: "#eef2f7",
                  boxShadow: "0px 1px 6px rgba(0,0,0,0.04)",
                  border: "1px solid #edf1f5",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize="15px"
                  mb={1.5}
                >
                  Duration
                </Typography>
                <Typography fontSize="13px" mb={1}>
                  <strong>Start Date:</strong> {item.start_date}
                </Typography>
                <Typography fontSize="13px">
                  <strong>End Date:</strong> {item.end_date}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: "14px",
                  backgroundColor: "#eef2f7",
                  boxShadow: "0px 1px 6px rgba(0,0,0,0.04)",
                  border: "1px solid #edf1f5",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize="15px"
                  mb={1.5}
                >
                  Trainer Details
                </Typography>
                <Typography fontSize="13px" mb={1}>
                  <strong>Trainer Name:</strong> {item.fullname}
                </Typography>
                <Typography fontSize="13px">
                  <strong>Email:</strong> {item.email}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Buttons below boxes — Upload Certificate (if not eligible) and Continue Learning */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 5,
              minHeight: "36px",
              alignItems: "center",
            }}
          >
            {loadingStatuses ? (
              <CircularProgress size={20} sx={{ color: "#1976d2" }} />
            ) : (
              <>
                {!isEligibleForDownload(item) &&
                  (item.faculty_id ? (buttonStatuses[item.faculty_id]?.upload_button ?? false) : true) && (
                    <Button
                      variant="contained"
                      onClick={() =>
                        navigate("/upload-certificate", {
                          state: { eventData: item },
                        })
                      }
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        backgroundColor: "#1976d2",
                        px: 3,
                        fontSize: "13px",
                        "&:hover": {
                          backgroundColor: "#1565c0",
                        },
                      }}
                    >
                      Upload Certificate
                    </Button>
                  )}

                {(item.faculty_id ? (buttonStatuses[item.faculty_id]?.continue_button ?? false) : true) && (
                  <Button
                    variant="contained"
                    disabled={loadingDomainId === item.domain_id}
                    onClick={() => handleContinueLearning(item.domain_id, item.bookslot_id)}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      backgroundColor: "#1976d2",
                      px: 3,
                      fontSize: "13px",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                    }}
                  >
                    {loadingDomainId === item.domain_id ? (
                      <>
                        <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                        Loading...
                      </>
                    ) : (
                      "Continue Learning"
                    )}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      ))}

      {/* Certificate Preview and Download Modal */}
      <Dialog
        open={certificateModal.open}
        onClose={handleCloseCertificateModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", minHeight: "80vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AwardIcon sx={{ color: "#2196F3" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Certificate Preview
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCertificateModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {certificateModal.loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 8,
              }}
            >
              <CircularProgress size={48} sx={{ color: "#2196F3" }} />
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Loading certificate...
              </Typography>
            </Box>
          ) : certificateModal.pdfUrl ? (
            <iframe
              src={`${certificateModal.pdfUrl}#toolbar=0`}
              style={{
                width: "100%",
                height: "70vh",
                border: "none",
              }}
              title="Certificate Preview"
            />
          ) : (
            <Typography variant="body2" sx={{ color: "#6B7280", py: 4 }}>
              Failed to load certificate
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={handleCloseCertificateModal}
            sx={{
              color: "#6B7280",
              "&:hover": { backgroundColor: "#F3F4F6" },
              borderRadius: "8px",
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownloadCertificate}
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!certificateModal.pdfUrl || certificateModal.loading}
            sx={{
              backgroundColor: "#4CAF50",
              "&:hover": { backgroundColor: "#45A049" },
              "&:disabled": { backgroundColor: "#CCCCCC" },
              borderRadius: "8px",
            }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FdpOngoing;